import { useEffect, useMemo, useState } from "react";

import { Box, Group, Loader, Text } from "@mantine/core";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";


import { DatePicker } from "components";
import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { KpiService } from "services";
import {
  KpiDashboardResponse,
  KpiMetricCatalogResponse,
  KpiPeriod,
  KpiTableResponse,
  KpiYearlyResponse,
  UserResponse,
} from "types";
import { AppUtils } from "utils";

import classes from "./kpi.module.css";
import { KPI_METRIC_ORDER } from "./kpiCatalog";
import KpiDistributionChart from "./KpiDistributionChart";
import KpiMetricSelector from "./KpiMetricSelector";
import KpiPeriodFilter from "./KpiPeriodFilter";
import KpiProgress from "./KpiProgress";
import KpiTable from "./KpiTable";
import KpiYearlyOverview from "./KpiYearlyOverview";

const {
  REQUEST_KPI_METRICS_ID,
  REQUEST_KPI_DASHBOARD_ID,
  REQUEST_KPI_YEARLY_ID,
  REQUEST_KPI_TABLE_ID,
  REQUEST_PENDING,
} = ServiceConstants;

interface KPITabProps {
  user?: UserResponse;
  // Reduced view for an agent looking at their own profile (dropdown + ring + table only).
  // Admin / team lead viewing another agent's profile sees the full dashboard.
  isAgentView?: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();

const formatDate = (date: Date | null) => (date ? moment(date).format("YYYY-MM-DD") : undefined);

const KPITab = ({ user, isAgentView = false }: KPITabProps) => {
  const { t } = useTranslation();

  const agentId = user?.id;

  const [metricKey, setMetricKey] = useState<string>("qualifications");
  const [period, setPeriod] = useState<KpiPeriod>("MONTHLY");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [year, setYear] = useState<number>(CURRENT_YEAR);

  const [metricKeys, setMetricKeys] = useState<string[]>(KPI_METRIC_ORDER as string[]);
  const [dashboard, setDashboard] = useState<KpiDashboardResponse | undefined>();
  const [table, setTable] = useState<KpiTableResponse | undefined>();
  const [yearly, setYearly] = useState<KpiYearlyResponse | undefined>();

  // CUSTOM period only takes effect once both dates are picked.
  const requestPeriod = period;
  const requestDateFrom = period === "CUSTOM" ? formatDate(dateFrom) : undefined;
  const requestDateTo = period === "CUSTOM" ? formatDate(dateTo) : undefined;
  const customReady = period !== "CUSTOM" || (!!requestDateFrom && !!requestDateTo);

  // ----- Fetch: metric catalog (once) -----
  useEffect(() => {
    KpiService.kpiMetrics();
  }, []);

  // ----- Fetch: dashboard + table on metric/period change -----
  useEffect(() => {
    if (!agentId || !customReady) {
      return;
    }
    KpiService.kpiDashboard(agentId, {
      metric: metricKey,
      period: requestPeriod,
      dateFrom: requestDateFrom,
      dateTo: requestDateTo,
    });
    KpiService.kpiTable(agentId, {
      period: requestPeriod,
      dateFrom: requestDateFrom,
      dateTo: requestDateTo,
    });
  }, [agentId, metricKey, requestPeriod, requestDateFrom, requestDateTo, customReady]);

  // ----- Fetch: yearly on metric/year change (admin view only) -----
  useEffect(() => {
    if (!agentId || isAgentView) {
      return;
    }
    KpiService.kpiYearly(agentId, { metric: metricKey, year });
  }, [agentId, isAgentView, metricKey, year]);

  // ----- Resolve service objects -----
  const metricsObject = useSelector((s: IRootState) => s.service.object[REQUEST_KPI_METRICS_ID]);
  const dashboardObject = useSelector((s: IRootState) => s.service.object[REQUEST_KPI_DASHBOARD_ID]);
  const tableObject = useSelector((s: IRootState) => s.service.object[REQUEST_KPI_TABLE_ID]);
  const yearlyObject = useSelector((s: IRootState) => s.service.object[REQUEST_KPI_YEARLY_ID]);

  useEffect(() => {
    AppUtils.resolveServiceObject(metricsObject, REQUEST_KPI_METRICS_ID, (data: KpiMetricCatalogResponse) => {
      if (data?.metrics?.length) {
        setMetricKeys(data.metrics.map((m) => m.key));
      }
    });
  }, [metricsObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(dashboardObject, REQUEST_KPI_DASHBOARD_ID, (data: KpiDashboardResponse) =>
      setDashboard(data),
    );
  }, [dashboardObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(tableObject, REQUEST_KPI_TABLE_ID, (data: KpiTableResponse) =>
      setTable(data),
    );
  }, [tableObject]);

  useEffect(() => {
    AppUtils.resolveServiceObject(yearlyObject, REQUEST_KPI_YEARLY_ID, (data: KpiYearlyResponse) =>
      setYearly(data),
    );
  }, [yearlyObject]);

  const isLoading = dashboardObject?.status === REQUEST_PENDING && !dashboard;

  // Dashboard has data when value > 0 or any subcategory has a value.
  const dashboardHasData = useMemo(
    () =>
      !!dashboard &&
      (dashboard.value > 0 || (dashboard.subcategories ?? []).some((s) => s.value > 0)),
    [dashboard],
  );
  const showDistribution = !!dashboard && (dashboard.subcategories?.length ?? 0) > 0;

  const selector = (variant: "pills" | "dropdown") => (
    <KpiMetricSelector
      metric={metricKey}
      onMetricChange={setMetricKey}
      metricKeys={metricKeys}
      variant={variant}
    />
  );

  const emptyState = (
    <Box className={classes.emptyState}>
      <Text>{t("profilePage.kpi.emptyState")}</Text>
    </Box>
  );

  const loadingState = (
    <Box className={classes.loadingState}>
      <Loader color="primary" />
    </Box>
  );

  // ----- Agent view: dropdown + yellow ring + table + od/do date range -----
  if (isAgentView) {
    return (
      <Box className={classes.root}>
        <Box className={classes.agentSelect}>{selector("dropdown")}</Box>

        {isLoading ? (
          loadingState
        ) : !dashboardHasData ? (
          emptyState
        ) : (
          <>
            <Box className={classes.agentRow}>
              <Box className={classes.agentLeft}>{dashboard && <KpiProgress dashboard={dashboard} variant="ring" />}</Box>
              <Box className={classes.agentTable}>
                <KpiTable table={table} />
              </Box>
            </Box>

            {/* Od / Do — single inline row (label beside input). */}
            <Group gap="lg" align="center" className={classes.agentDateRange}>
              <Group gap="xs" align="center" wrap="nowrap">
                <Text size="sm" c="#636A79">
                  {t("profilePage.kpi.period.from")}
                </Text>
                <DatePicker
                  value={dateFrom}
                  onChange={(value: Date | null) => {
                    setPeriod("CUSTOM");
                    setDateFrom(value);
                  }}
                  clearable
                  mb={0}
                />
              </Group>
              <Group gap="xs" align="center" wrap="nowrap">
                <Text size="sm" c="#636A79">
                  {t("profilePage.kpi.period.to")}
                </Text>
                <DatePicker
                  value={dateTo}
                  onChange={(value: Date | null) => {
                    setPeriod("CUSTOM");
                    setDateTo(value);
                  }}
                  clearable
                  mb={0}
                />
              </Group>
            </Group>
          </>
        )}
      </Box>
    );
  }

  // ----- Admin view: pills + pie + progress + yearly + table -----
  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        {selector("pills")}
        <KpiPeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
        />
      </Box>

      {isLoading ? (
        loadingState
      ) : !dashboardHasData ? (
        emptyState
      ) : (
        <>
          <Box className={showDistribution ? classes.topRow : classes.topRowFull}>
            {showDistribution && dashboard ? <KpiDistributionChart dashboard={dashboard} /> : null}
            {dashboard && <KpiProgress dashboard={dashboard} />}
          </Box>

          {yearly && <KpiYearlyOverview yearly={yearly} year={year} onYearChange={setYear} />}

          <KpiTable table={table} />
        </>
      )}
    </Box>
  );
};

export default KPITab;
